// ─── GlobalSearch.jsx ─────────────────────────────────────────────────────────
// Full-screen command-palette style search across all platform data.
// Cmd/Ctrl+K from any page to open. Two-panel: results list + detail preview.

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabaseStorageCompat as supabase } from "./fileStorage";
import { db } from "./dbClient";
import { useTheme } from "./theme";

// ── Search config — one entry per searchable module ───────────────────────────
// `keywords`    : typing any of these returns ALL recent records from this module
// `matchFields` : columns searched with OR ilike when user types a name/value
// `labelFn`     : optional fn(row) => string for cases where label isn't a single column
const MODULES = [
  {
    label: "Case",        type: "case",       nav: "cases",
    keywords: ["case", "cases", "case management"],
    table: "cases",
    matchFields: ["entity", "type", "status", "country", "notes", "assigned_to"],
    select: "id,entity,type,risk,status,notes,country,assigned_to,created_at",
    detail: r => ([
      { label: "Entity",    value: r.entity },
      { label: "Type",      value: r.type },
      { label: "Risk",      value: r.risk,      color: riskColor(r.risk) },
      { label: "Status",    value: r.status },
      { label: "Country",   value: r.country },
      { label: "Assigned",  value: r.assigned_to },
      { label: "Opened",    value: r.created_at ? r.created_at.slice(0,10) : null },
      { label: "Notes",     value: r.notes, prose: true },
    ]),
  },
  {
    label: "TM Alert",    type: "tm_alert",   nav: "tm",
    keywords: ["tm", "alert", "alerts", "transaction monitoring", "aml", "bsa"],
    table: "alerts",
    matchFields: ["entity", "type", "status", "country", "notes"],
    select: "id,entity,type,risk,status,amount,country,notes,date",
    detail: r => ([
      { label: "Entity",    value: r.entity },
      { label: "Type",      value: r.type },
      { label: "Risk",      value: r.risk,      color: riskColor(r.risk) },
      { label: "Status",    value: r.status },
      { label: "Amount",    value: r.amount },
      { label: "Country",   value: r.country },
      { label: "Date",      value: r.date },
      { label: "Notes",     value: r.notes, prose: true },
    ]),
  },
  {
    label: "Transaction",  type: "transaction", nav: "tm",
    keywords: ["transaction", "transactions", "wire", "cash", "crypto", "payment", "transfer"],
    table: "transactions",
    matchFields: ["entity", "type", "country", "notes", "status"],
    select: "id,entity,type,amount,country,status,date,notes,risk",
    detail: r => ([
      { label: "Entity",    value: r.entity },
      { label: "Type",      value: r.type },
      { label: "Amount",    value: r.amount },
      { label: "Country",   value: r.country },
      { label: "Risk",      value: r.risk,      color: riskColor(r.risk) },
      { label: "Status",    value: r.status },
      { label: "Date",      value: r.date },
      { label: "Notes",     value: r.notes, prose: true },
    ]),
  },
  {
    // kyc_submissions IS the customer table — full_name is split into first_name + last_name
    label: "KYC / Customer",   type: "kyc",   nav: "onboarding",
    keywords: ["kyc", "onboarding", "know your customer", "customer", "customers", "client", "onboard", "submission"],
    table: "kyc_submissions",
    matchFields: ["first_name", "last_name", "email", "nationality", "status", "review_note"],
    select: "id,first_name,last_name,type,status,risk_rating,nationality,email,review_note,submitted_at",
    labelFn: r => [r.first_name, r.last_name].filter(Boolean).join(" ") || r.email || r.id,
    detail: r => ([
      { label: "Name",        value: [r.first_name, r.last_name].filter(Boolean).join(" ") },
      { label: "Entity Type", value: r.type },
      { label: "Status",      value: r.status },
      { label: "Risk",        value: r.risk_rating,  color: riskColor(r.risk_rating) },
      { label: "Nationality", value: r.nationality },
      { label: "Email",       value: r.email },
      { label: "Submitted",   value: r.submitted_at ? r.submitted_at.slice(0,10) : null },
      { label: "Review Note", value: r.review_note, prose: true },
    ]),
  },
  {
    label: "SAR",        type: "sar",    nav: "sar",
    keywords: ["sar", "suspicious activity", "suspicious activity report", "filing"],
    table: "sar_drafts",
    matchFields: ["subject_name", "status", "suspicious_activity_type", "assigned_to", "jurisdiction"],
    select: "id,subject_name,status,suspicious_activity_type,filed_ref,assigned_to,updated_at",
    detail: r => ([
      { label: "Subject",          value: r.subject_name },
      { label: "Status",           value: r.status },
      { label: "Activity Type",    value: r.suspicious_activity_type },
      { label: "Filed Ref",        value: r.filed_ref },
      { label: "Assigned To",      value: r.assigned_to },
      { label: "Last Updated",     value: r.updated_at ? r.updated_at.slice(0,10) : null },
    ]),
  },
  {
    label: "EDD",        type: "edd",    nav: "edd",
    keywords: ["edd", "enhanced due diligence", "due diligence", "enhanced"],
    table: "edd_reviews",
    matchFields: ["customer_name", "status", "decision", "assigned_to"],
    select: "id,customer_name,status,decision,assigned_to,reviewed_by,review_date",
    detail: r => ([
      { label: "Customer",    value: r.customer_name },
      { label: "Status",      value: r.status },
      { label: "Decision",    value: r.decision },
      { label: "Assigned To", value: r.assigned_to },
      { label: "Reviewed By", value: r.reviewed_by },
      { label: "Review Date", value: r.review_date ? r.review_date.slice(0,10) : null },
    ]),
  },
  {
    label: "Sanctions",           type: "sanctions",  nav: "kyc",
    keywords: ["sanctions", "sanctions screening", "ofac", "screening", "pep", "politically exposed"],
    table: "sanctions_screening",
    matchFields: ["subject_name", "result", "subject_nationality"],
    select: "id,subject_name,result,risk_score,match_count,subject_nationality,screen_type",
    detail: r => ([
      { label: "Subject",      value: r.subject_name },
      { label: "Result",       value: r.result, color: r.result === "HIT" ? "#FF6B6B" : "#00E676" },
      { label: "Screen Type",  value: r.screen_type },
      { label: "Risk Score",   value: r.risk_score },
      { label: "Matches",      value: r.match_count },
      { label: "Nationality",  value: r.subject_nationality },
    ]),
  },
  {
    label: "Watchlist",          type: "watchlist",  nav: "watchlist",
    keywords: ["watchlist", "watch list", "blacklist", "internal watchlist"],
    table: "internal_watchlist",
    matchFields: ["full_name", "list_type", "reason"],
    select: "id,full_name,list_type,reason",
    detail: r => ([
      { label: "Name",      value: r.full_name },
      { label: "List Type", value: r.list_type },
      { label: "Reason",    value: r.reason, prose: true },
    ]),
  },
  {
    label: "CTR Filing",  type: "ctr",  nav: "ctr",
    keywords: ["ctr", "currency transaction", "currency transaction report", "cash report"],
    table: "ctr_filings",
    matchFields: ["subject_name", "status", "jurisdiction"],
    select: "id,subject_name,status,transaction_amount,transaction_date,filed_date",
    detail: r => ([
      { label: "Subject",    value: r.subject_name },
      { label: "Status",     value: r.status },
      { label: "Tx Amount",  value: r.transaction_amount },
      { label: "Tx Date",    value: r.transaction_date },
      { label: "Filed",      value: r.filed_date },
    ]),
  },
  {
    label: "Reg Report",        type: "regreport",   nav: "regreporting",
    keywords: ["reg report", "regulatory report", "report", "regulatory", "reporting", "annual", "aml annual"],
    table: "regulatory_reports",
    matchFields: ["title", "type", "status", "period", "created_by"],
    select: "id,title,type,status,period,created_by,updated_at",
    detail: r => ([
      { label: "Title",      value: r.title },
      { label: "Type",       value: r.type },
      { label: "Status",     value: r.status },
      { label: "Period",     value: r.period },
      { label: "Created By", value: r.created_by },
      { label: "Updated",    value: r.updated_at ? r.updated_at.slice(0,10) : null },
    ]),
  },
  {
    label: "Training Module",  type: "training",  nav: "training",
    keywords: ["training", "training module", "course", "courses", "learning", "compliance training"],
    table: "training_modules",
    matchFields: ["title", "category"],
    select: "id,title,category,level,duration",
    detail: r => ([
      { label: "Title",    value: r.title },
      { label: "Category", value: r.category },
      { label: "Level",    value: r.level },
      { label: "Duration", value: r.duration },
    ]),
  },
  {
    label: "To-Do",  type: "todo",  nav: "todo",
    keywords: ["todo", "to-do", "task", "tasks", "to do"],
    table: "todos",
    matchFields: ["text", "assignee_name", "creator_name"],
    select: "id,text,done,priority,assignee_name,creator_name",
    labelFn: r => r.text || r.id,
    detail: r => ([
      { label: "Task",       value: r.text },
      { label: "Status",     value: r.done ? "Done ✓" : "Pending" },
      { label: "Priority",   value: r.priority },
      { label: "Assigned To",value: r.assignee_name },
      { label: "Created By", value: r.creator_name },
    ]),
  },
];

const MODULE_COLOR = {
  case: "#FF6B6B", tm_alert: "#F0B429", transaction: "#F0B429",
  customer: "#00E676", kyc: "#00B4FF", sar: "#A78BFA",
  edd: "#FF8C00", sanctions: "#FF4444", watchlist: "#FF4444",
  ctr: "#FF8C00", regreport: "#00B4FF", training: "#00E676", todo: "#A78BFA",
};

function riskColor(risk) {
  if (!risk) return null;
  const r = risk.toUpperCase();
  if (r === "CRITICAL") return "#FF6B6B";
  if (r === "HIGH")     return "#FF4444";
  if (r === "MEDIUM")   return "#F0B429";
  if (r === "LOW")      return "#00E676";
  return null;
}

// ── Detail panel for a selected result ───────────────────────────────────────
function DetailPanel({ result, onNavigate, onClose, C }) {
  const mod = MODULES.find(m => m.type === result._type);
  if (!mod) return null;
  const fields = mod.detail(result._raw).filter(f => f.value != null && f.value !== "");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{
            fontSize: 10, fontFamily: "'DM Mono',monospace",
            color: MODULE_COLOR[result._type] || C.accent,
            background: `${MODULE_COLOR[result._type] || C.accent}18`,
            padding: "3px 8px", borderRadius: 4,
          }}>
            {mod.label.toUpperCase()}
          </span>
          <span style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
            {result._raw.id}
          </span>
        </div>
        <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>
          {result._label}
        </div>
      </div>

      {/* Fields */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        {fields.map((f, i) => (
          <div key={i} style={{ marginBottom: f.prose ? 16 : 12 }}>
            <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.1em", fontFamily: "'DM Mono',monospace", marginBottom: 3 }}>
              {f.label.toUpperCase()}
            </div>
            {f.prose ? (
              <div style={{ color: C.textMid, fontSize: 12, fontFamily: "'DM Mono',monospace", lineHeight: 1.6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px" }}>
                {String(f.value).slice(0, 400)}{String(f.value).length > 400 ? "…" : ""}
              </div>
            ) : (
              <div style={{ color: f.color || C.text, fontSize: 13, fontWeight: f.color ? 600 : 400 }}>
                {String(f.value)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer: navigate button */}
      <div style={{ padding: "14px 24px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 10 }}>
        <button
          onClick={() => { onNavigate(result._nav); onClose(); }}
          style={{
            flex: 1, padding: "10px 16px", borderRadius: 8, cursor: "pointer",
            background: C.accent, border: "none", color: "#000",
            fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700,
          }}
        >
          Open in {mod.label} →
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function GlobalSearch({ company, onNavigate }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [open,     setOpen]     = useState(false);
  const [selected, setSelected] = useState(null);   // selected result for detail panel
  const [focused,  setFocused]  = useState(false);
  const inputRef   = useRef();
  const overlayRef = useRef();

  function closeOverlay() {
    setOpen(false);
    setQuery("");
    setResults([]);
    setSelected(null);
  }

  // Cmd/Ctrl+K
  useEffect(() => {
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") closeOverlay();
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q.trim() || !company?.id) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const qLower = q.trim().toLowerCase();
    // NOTE: .or() filter strings use * as wildcard; direct .ilike() uses %
    const star = `*${q.trim()}*`;
    const pct  = `%${q.trim()}%`;

    const keywordModules = MODULES.filter(m =>
      (m.keywords || []).some(kw => kw === qLower || qLower.startsWith(kw))
    );
    const isKeyword = keywordModules.length > 0;

    // Run each module query independently so one failure doesn't wipe all results
    async function queryModule(m) {
      try {
        if (isKeyword) {
          if (!keywordModules.includes(m)) return [];
          const { data } = await db
            .from(m.table).select(m.select)
            .eq("company_id", company.id).limit(8).execute();
          return data || [];
        } else {
          const fields = m.matchFields || [];
          if (fields.length === 0) return [];

          if (fields.length === 1) {
            // Single field — safe direct .ilike()
            const { data } = await db
              .from(m.table).select(m.select)
              .eq("company_id", company.id)
              .ilike(fields[0], pct)
              .limit(5).execute();
            return data || [];
          } else {
            // Multiple fields — OR filter uses * wildcard
            const orFilter = fields.map(f => `${f}.ilike.${star}`).join(",");
            const { data, error } = await db
              .from(m.table).select(m.select)
              .eq("company_id", company.id)
              .or(orFilter)
              .limit(5).execute();
            if (error) {
              // Fallback: just search the primary field
              const result = await db
                .from(m.table).select(m.select)
                .eq("company_id", company.id)
                .ilike(fields[0], pct)
                .limit(5).execute();
              return result.data || [];
            }
            return data || [];
          }
        }
      } catch (e) {
        console.warn(`[GlobalSearch] ${m.table}:`, e.message);
        return [];
      }
    }

    const allRows = await Promise.all(MODULES.map(queryModule));

    const combined = [];
    MODULES.forEach((m, i) => {
      const rows = allRows[i];
      if (rows.length) console.debug(`[GlobalSearch] ${m.table}: ${rows.length} rows`);
      rows.forEach(row => {
        const label = m.labelFn
          ? m.labelFn(row)
          : (m.matchFields || []).map(f => row[f]).find(v => v) || row.id || "—";
        combined.push({ _type: m.type, _label: label, _nav: m.nav, _raw: row });
      });
    });

    setResults(combined);
    setSelected(combined.length > 0 ? combined[0] : null);
    setLoading(false);
  }, [company?.id]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 240);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  // Sidebar trigger button (always visible)
  const sidebarTrigger = (
    <button
      onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 7,
        background: C.bg,
        border: `1px solid ${focused ? C.accent : C.border}`,
        borderRadius: 8, padding: "7px 10px",
        cursor: "text", marginBottom: 6, transition: "border-color 0.15s",
      }}
    >
      <span style={{ color: C.textDim, fontSize: 12 }}>🔍</span>
      <span style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono',monospace", flex: 1, textAlign: "left" }}>
        {t('globalSearch.placeholder')}
      </span>
      <span style={{ color: C.textDim, fontSize: 9, fontFamily: "'DM Mono',monospace", opacity: 0.6, flexShrink: 0 }}>⌘K</span>
    </button>
  );

  return (
    <>
      {sidebarTrigger}

      {/* ── Full-screen overlay ── */}
      {open && (
        <div
          ref={overlayRef}
          onClick={e => { if (e.target === overlayRef.current) closeOverlay(); }}
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(0,0,0,0.72)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            paddingTop: "10vh",
          }}
        >
          <div style={{
            width: "min(860px, 92vw)", maxHeight: "76vh",
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 14, boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            {/* Search input row */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "14px 18px", borderBottom: `1px solid ${C.border}`,
            }}>
              <span style={{ color: C.accent, fontSize: 16, flexShrink: 0 }}>🔍</span>
              <input
                ref={inputRef}
                value={query}
                onChange={e => { setQuery(e.target.value); setSelected(null); }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Search cases, customers, transactions, alerts, SAR, EDD, watchlist…"
                autoFocus
                style={{
                  flex: 1, background: "transparent", border: "none", outline: "none",
                  color: C.text, fontSize: 15, fontFamily: "'DM Mono',monospace",
                }}
              />
              {loading && <span style={{ color: C.textDim, fontSize: 11 }}>Searching…</span>}
              <button onClick={closeOverlay} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, padding: "3px 8px", color: C.textDim, fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
                esc
              </button>
            </div>

            {/* Body: results list + detail panel */}
            <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
              {/* Results list */}
              <div style={{
                width: 300, borderRight: `1px solid ${C.border}`,
                overflowY: "auto", flexShrink: 0,
              }}>
                {!query.trim() && (
                  <div style={{ padding: "16px 14px" }}>
                    <div style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace", marginBottom: 10 }}>Search everything, or jump to a module:</div>
                    {[
                      { kw: "cases",        label: "Cases",           color: "#FF6B6B" },
                      { kw: "edd",          label: "EDD Reviews",     color: "#FF8C00" },
                      { kw: "sar",          label: "SAR Filings",     color: "#A78BFA" },
                      { kw: "kyc",          label: "KYC / Onboarding",color: "#00B4FF" },
                      { kw: "sanctions",    label: "Sanctions",       color: "#FF4444" },
                      { kw: "watchlist",    label: "Watchlist",       color: "#FF4444" },
                      { kw: "ctr",          label: "CTR Filings",     color: "#FF8C00" },
                      { kw: "transactions", label: "Transactions",    color: "#F0B429" },
                      { kw: "training",     label: "Training",        color: "#00E676" },
                      { kw: "reg report",   label: "Reg Reports",     color: "#00B4FF" },
                    ].map(s => (
                      <button key={s.kw} onClick={() => { setQuery(s.kw); }}
                        style={{ display: "inline-block", margin: "3px", padding: "4px 10px", borderRadius: 5, border: `1px solid ${s.color}44`, background: `${s.color}0f`, color: s.color, fontSize: 10, fontFamily: "'DM Mono',monospace", cursor: "pointer" }}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
                {query.trim() && !loading && results.length === 0 && (
                  <div style={{ padding: "20px 18px", color: C.textDim, fontSize: 12, fontFamily: "'DM Mono',monospace", textAlign: "center" }}>
                    No results for "{query}"
                  </div>
                )}

                {/* Group by module */}
                {(() => {
                  const groups = {};
                  results.forEach(r => {
                    const mod = MODULES.find(m => m.type === r._type);
                    if (!groups[r._type]) groups[r._type] = { label: mod?.label || r._type, items: [] };
                    groups[r._type].items.push(r);
                  });
                  return Object.entries(groups).map(([type, group]) => (
                    <div key={type}>
                      <div style={{
                        padding: "6px 14px 4px",
                        fontSize: 9, letterSpacing: "0.1em",
                        color: MODULE_COLOR[type] || C.accent,
                        fontFamily: "'DM Mono',monospace",
                        background: `${MODULE_COLOR[type] || C.accent}0a`,
                        borderBottom: `1px solid ${C.border}`,
                        borderTop: `1px solid ${C.border}`,
                      }}>
                        {group.label.toUpperCase()}
                      </div>
                      {group.items.map(r => {
                        const isSel = selected === r;
                        return (
                          <button
                            key={`${r._type}-${r._raw.id}`}
                            onClick={() => setSelected(r)}
                            style={{
                              width: "100%", display: "block", textAlign: "left",
                              padding: "10px 14px", border: "none", cursor: "pointer",
                              borderBottom: `1px solid ${C.border}`,
                              background: isSel ? `${MODULE_COLOR[type] || C.accent}18` : "transparent",
                              borderLeft: isSel ? `3px solid ${MODULE_COLOR[type] || C.accent}` : "3px solid transparent",
                              transition: "background 0.1s",
                            }}
                            onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = C.surfaceAlt; }}
                            onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                          >
                            <div style={{ color: C.text, fontSize: 12, fontWeight: isSel ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {r._label}
                            </div>
                            <div style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {[r._raw.type, r._raw.status, r._raw.risk, r._raw.risk_level, r._raw.result].filter(Boolean).slice(0, 2).join(" · ") || r._raw.id}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ));
                })()}

                {results.length > 0 && (
                  <div style={{ padding: "8px 14px", fontSize: 10, color: C.textDim, fontFamily: "'DM Mono',monospace", textAlign: "center" }}>
                    {results.length} result{results.length !== 1 ? "s" : ""}
                  </div>
                )}
              </div>

              {/* Detail panel */}
              <div style={{ flex: 1, overflowY: "auto" }}>
                {selected ? (
                  <DetailPanel result={selected} onNavigate={onNavigate} onClose={closeOverlay} C={C} />
                ) : (
                  <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 12, fontFamily: "'DM Mono',monospace", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 28, opacity: 0.3 }}>◈</div>
                    <div>Select a result to preview</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
